/**
 * Notification — 顶部下滑通知组件
 *
 * 用法：
 *   window.notify('消息内容');
 *   window.notify('操作成功！', { type: 'success' });
 *   window.notify('网络错误', { type: 'error', duration: 0 });
 *
 * 选项 (NotifyOptions)：
 *   type      : 'success' | 'error' | 'warning' | 'info'  (默认 'info')
 *   duration  : number   ms 后自动关闭，0 = 不自动关闭      (默认 3500)
 *   closable  : boolean  是否显示关闭按钮                   (默认 true)
 *   icon      : string | false  自定义 SVG 或 false 隐藏图标 (默认按 type)
 */

(function () {
  'use strict';

  // ── 内置图标 SVG（按 type） ────────────────────────────────────────────────



  // ── CSS 延迟加载 ───────────────────────────────────────────────────────────

  let _cssLoaded = false;

  function loadCSS() {
    if (_cssLoaded) return;
    _cssLoaded = true;

    // 从当前脚本标签所在目录推断 CSS URL
    const scriptSrc =
      (document.currentScript && document.currentScript.src) ||
      (() => {
        // 降级：遍历所有 script 找到本文件
        const scripts = Array.from(document.querySelectorAll('script[src]'));
        const match = scripts.find((s) => s.src.includes('notification.js'));
        return match ? match.src : '';
      })();

    const cssUrl = scriptSrc
      ? scriptSrc.replace('notification.js', 'notification.css')
      : null;

    if (cssUrl) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);
    }
  }

  // ── NotificationManager 单例 ───────────────────────────────────────────────

  const NotificationManager = (() => {
    /** @type {HTMLElement|null} */
    let container = null;

    /** 通知计数，用于生成唯一 ID */
    let _idCounter = 0;

    /** 获取或创建容器 */
    function getContainer() {
      if (!container) {
        container = document.createElement('div');
        container.id = 'notification-container';
        container.setAttribute('role', 'region');
        container.setAttribute('aria-label', 'Notifications');
        container.setAttribute('aria-live', 'polite');
      }
      return container;
    }

    /**
     * 显示一条通知
     * @param {string} message
     * @param {object} options
     */
    function show(message, options = {}) {
      const {
        type = 'info',
        duration = 3500,
      } = options;

      loadCSS();

      const id = `notification-${++_idCounter}`;
      const el = document.createElement('div');

      el.id = id;
      el.className = `notification notification--${type}`;
      el.setAttribute('role', 'alert');

      el.innerHTML = `<span class="notification__body">${message}</span>`;

      // 每次显示新的通知时，将整个容器移到 body 最底部，确保层级最高
      document.body.appendChild(getContainer());
      getContainer().appendChild(el);

      // 强制 reflow，再加 is-visible 触发过渡
      void el.offsetHeight;
      el.classList.add('is-visible');

      // 自动关闭
      if (duration > 0) {
        setTimeout(() => dismiss(el), duration);
      }

      return {
        id,
        dismiss: () => dismiss(el),
      };
    }

    /**
     * 关闭一条通知（带退出动画）
     * @param {HTMLElement} el
     */
    function dismiss(el) {
      if (!el || el.classList.contains('is-hiding')) return;
      el.classList.remove('is-visible');
      el.classList.add('is-hiding');

      // 等动画结束后移除
      el.addEventListener(
        'transitionend',
        () => {
          el.remove();
        },
        { once: true }
      );

      // 降级：若 transitionend 没触发，250ms 后强制移除
      setTimeout(() => el.remove(), 350);
    }

    return { show };
  })();

  // ── 全局 API ───────────────────────────────────────────────────────────────

  /**
   * 显示一条通知
   * @param {string} message        通知文本内容
   * @param {object} [options]      可选配置项，见文件顶部 JSDoc
   * @returns {{ id: string, dismiss: Function }}
   */
  window.notify = function notify(message, options) {
    return NotificationManager.show(String(message), options || {});
  };

  // 同时挂载到 window.Notification 命名空间，避免与原生 Notification API 冲突
  window.AppNotification = NotificationManager;
})();
